import { Avatar, Checkbox, Paper, Stack, Typography } from "@mui/material";
import { makeTintedBackgroundFromColor } from "@entrilia/shared/utilities/colorHelper";
import type { DataImportTarget } from "../../../../../api/types/dataImportTypes";
import type { ImportCsvTargetOption } from "../importCsvDataPageState";
import ImportCsvTargetIcon from "./ImportCsvTargetIcon";

interface Props {
  targetOption: ImportCsvTargetOption;
  onToggleSelected: (newSelected: boolean) => void;
}

const dataImportTargetDescriptions: Record<DataImportTarget, string> = {
  PlatformObjects: "Create and update platform objects",
  PortfolioMonitoringMetrics: "Create and update metrics for Portfolio Monitoring",
  PortfolioMonitoringMetricValues: "Create metric values for Portfolio Monitoring",
  DocumentCollections: "Create document collections",
  Contacts: "Add, update or delete contacts. Rows are matched by email.",
  ContactAccess: "Set each contact's role, primary flag, email notifications and categories on a fund investor.",
  Investors:
    'Create and update investors. A row with a value in the "No" column updates that investor; a row without one creates a new investor.',
  CommitmentDrafts:
    "Create draft investor commitments in Fund Accounting, one per row. Each amount is a change to the commitment in the fund's local currency, not the new total. Drafts are not posted.",
};

const ImportCsvTargetCard = ({ targetOption, onToggleSelected }: Props) => {
  return (
    <Paper variant="outlined" sx={{ p: 1 }}>
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <Avatar
            variant="rounded"
            sx={(t) => ({
              width: 32,
              height: 32,
              color: targetOption.selected ? t.palette.primary.main : t.palette.secondary.main,
              bgcolor: makeTintedBackgroundFromColor(
                t,
                targetOption.selected ? t.palette.primary.main : t.palette.secondary.main
              ),
            })}
            color={targetOption.selected ? "primary" : "secondary"}
          >
            <ImportCsvTargetIcon target={targetOption.target} />
          </Avatar>
          <Stack spacing={0.5}>
            <Typography variant="subtitle2">{targetOption.label}</Typography>
            <Typography color="textSecondary">{dataImportTargetDescriptions[targetOption.target]}</Typography>
          </Stack>
        </Stack>
        <Checkbox checked={targetOption.selected} onChange={(_, checked) => onToggleSelected(checked)} />
      </Stack>
    </Paper>
  );
};

export default ImportCsvTargetCard;
